import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { DocumentType } from '@superbet-group/web.lib.config';

import {
    verifyIdentityEntrypoints,
    GeneralEventDto,
    IntroEventDto,
    EntryEventDto,
    SelectDocumentEventDto,
    ImageUploadEventDto,
    PhotoDeliveryMethods,
    ImageUploadSecondEventPayload,
    EventParamsDto,
} from '@accountFederation/modules/idCardVerification/types';

import {
    generateEntryPayload,
    generateIntroPayload,
    generateGeneralPayload,
    generateSelectDocumentPayload,
    generateImageUploadPayload,
    generateImageUploadSecondStepStartedPayload,
} from './generatePayloads';

export const AnalyticsKycEvent = {
    VerifyIdentityCompletedRegistration: 'VerifyIdentity_CompletedRegistration',
    VerifyIdentitySubmitted: 'VerifyIdentity_Submitted',
    VerifyIdentityClickedFinish: 'VerifyIdentity_ClickedFinish',
    VerifyIdentityClickedUploadAdditional: 'VerifyIdentity_ClickedUploadAdditional',
    VerifyIdentityVerifiedNotification: 'VerifyIdentity_VerifiedNotification',
    VerifyIdentityEntry: 'VerifyIdentity_Entry',
    VerifyIdentityIntro: 'VerifyIdentity_Intro',
    VerifyIdentitySelectDocument: 'VerifyIdentity_SelectDocument',
    VerifyIdentityImageUpload: 'VerifyIdentity_ImageUpload',
    VerifyIdentityImageUploadSecondStepStarted: 'VerifyIdentity_ImageUploadSecondStepStarted',
} as const;
export type AnalyticsKycEvent = typeof AnalyticsKycEvent[keyof typeof AnalyticsKycEvent];

const EventsMapper = {
    [AnalyticsKycEvent.VerifyIdentityCompletedRegistration]:
        ({ generalPayload }: { generalPayload: GeneralEventDto }): GeneralEventDto => {
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityCompletedRegistration, generalPayload);
            return generalPayload;
        },
    [AnalyticsKycEvent.VerifyIdentitySubmitted]:
        ({ generalPayload }: { generalPayload: GeneralEventDto }): GeneralEventDto => {
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentitySubmitted, generalPayload);
            return generalPayload;
        },
    [AnalyticsKycEvent.VerifyIdentityClickedFinish]:
        ({ generalPayload }: { generalPayload: GeneralEventDto }): GeneralEventDto => {
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityClickedFinish, generalPayload);
            return generalPayload;
        },
    [AnalyticsKycEvent.VerifyIdentityClickedUploadAdditional]:
        ({ generalPayload }: { generalPayload: GeneralEventDto }): GeneralEventDto => {
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityClickedUploadAdditional, generalPayload);
            return generalPayload;
        },
    [AnalyticsKycEvent.VerifyIdentityVerifiedNotification]:
        ({ generalPayload }: { generalPayload: GeneralEventDto }): GeneralEventDto => {
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityVerifiedNotification, generalPayload);
            return generalPayload;
        },
    [AnalyticsKycEvent.VerifyIdentityEntry]:
        (
            {
                generalPayload,
                source,
                params,
            }:
            {
                generalPayload: GeneralEventDto;
                source: verifyIdentityEntrypoints | undefined;
                params: EventParamsDto;
            },
        ): EntryEventDto => {
            const payload = generateEntryPayload(generalPayload, source, params.user);
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityEntry, payload);
            return payload;
        },
    [AnalyticsKycEvent.VerifyIdentityIntro]:
        (
            {
                generalPayload,
                params,
            }:
            {
                generalPayload: GeneralEventDto;
                params: EventParamsDto;
            },
        ): IntroEventDto => {
            const payload = generateIntroPayload(generalPayload, params.user);
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityIntro, payload);
            return payload;
        },
    [AnalyticsKycEvent.VerifyIdentitySelectDocument]:
        (
            {
                generalPayload,
                type,
                params,
            }:
            {
                generalPayload: GeneralEventDto;
                type: DocumentType | undefined;
                params: EventParamsDto;
            },
        ):
        SelectDocumentEventDto => {
            const payload = generateSelectDocumentPayload(generalPayload, type, params.user);
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentitySelectDocument, payload);
            return payload;
        },
    [AnalyticsKycEvent.VerifyIdentityImageUpload]:
        (
            {
                generalPayload,
                type,
                method,
            }:
            {
                generalPayload: GeneralEventDto;
                type: DocumentType | undefined;
                method: PhotoDeliveryMethods | undefined;
            },
        ): ImageUploadEventDto => {
            const payload = generateImageUploadPayload(generalPayload, type, method);
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityImageUpload, payload);
            return payload;
        },
    [AnalyticsKycEvent.VerifyIdentityImageUploadSecondStepStarted]:
        (
            {
                generalPayload,
                type,
            }:
            {
                generalPayload: GeneralEventDto;
                type: DocumentType | undefined;
            },
        ): ImageUploadSecondEventPayload => {
            const payload = generateImageUploadSecondStepStartedPayload(generalPayload, type);
            AnalyticsSDK.getInstance().log(AnalyticsKycEvent.VerifyIdentityImageUploadSecondStepStarted, payload);
            return payload;
        },
};

type EventsMapperType = keyof typeof EventsMapper;

export interface VerifyIdentityData {
    eventName: EventsMapperType;
    source?: verifyIdentityEntrypoints;
    type?: DocumentType;
    method?: PhotoDeliveryMethods;
    idVerificationStoreGetters: EventParamsDto;
}

export function sendVerifyIdentityEvent({
    eventName,
    source,
    type,
    method,
    idVerificationStoreGetters,
}: VerifyIdentityData) {
    const generalPayload = generateGeneralPayload(idVerificationStoreGetters);
    return EventsMapper[eventName]({ generalPayload, source, type, method, params: idVerificationStoreGetters });
}
